import { API_BASE_URL, buildApiUrl, ENDPOINTS, getAuthHeaders } from '../config/api.config';

/**
 * Service for handling streaming CSV imports with progress tracking
 */
export class StreamingImportService {
  private authToken: string | null = null;

  /**
   * Set the authentication token for API requests
   */
  setAuthToken(token: string) {
    this.authToken = token;
  }

  /**
   * Get authentication headers for requests
   */
  private getHeaders(): HeadersInit {
    const token = this.authToken || localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  /**
   * Initialize a new streaming import session
   */
  async initializeImport(importType: 'sales_data' | 'ad_spend_data', fileName: string, fileSize: number): Promise<Record<string, unknown>> {
    try {
      // Use the ENDPOINTS constant and buildApiUrl function for consistent URL construction
      const endpoint = `${ENDPOINTS.STREAMING_IMPORTS.UPLOAD('initialize')}`;
      const url = buildApiUrl(endpoint);

      console.log(`Initializing streaming import at: ${url}`);

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          importType,
          filename: fileName,
          fileSize,
          contentType: 'text/csv'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to initialize import');
      }

      return await response.json();
    } catch (error) {
      console.error('Error initializing import:', error);
      throw error;
    }
  }

  /**
   * Upload a file with progress tracking
   */
  async uploadFile(file: File, importId: string, onProgress?: (progress: number) => void): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use ENDPOINTS constant for consistent URL construction
      const endpoint = `${ENDPOINTS.STREAMING_IMPORTS.PROCESS(importId)}/upload`;
      const url = buildApiUrl(endpoint);

      console.log(`Uploading file to streaming endpoint: ${url}`);

      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Setup progress tracking
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        });

        // Handle response
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (e) {
              reject(new Error('Invalid response format'));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };

        // Handle errors
        xhr.onerror = () => {
          reject(new Error('Network error occurred during upload'));
        };

        // Start upload
        xhr.open('POST', url);
        const token = this.authToken || localStorage.getItem('authToken');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  /**
   * Submit column mapping for a streaming import
   */
  async submitColumnMapping(importId: string, columnMapping: Record<string, string>): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.STREAMING_IMPORTS.PROCESS(importId)}/map`;
      const url = buildApiUrl(endpoint);

      console.log(`Submitting column mapping to: ${url}`);

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ columnMapping })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit column mapping');
      }

      return await response.json();
    } catch (error) {
      console.error('Error submitting column mapping:', error);
      throw error;
    }
  }

  /**
   * Start processing a CSV file
   */
  async startProcessing(importId: string, batchSize = 100): Promise<Record<string, unknown>> {
    try {
      const endpoint = ENDPOINTS.STREAMING_IMPORTS.PROCESS(importId);
      const url = buildApiUrl(endpoint);

      console.log(`Starting processing at: ${url}`);

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ batchSize })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to start processing');
      }

      return await response.json();
    } catch (error) {
      console.error('Error starting processing:', error);
      throw error;
    }
  }

  /**
   * Cancel an in-progress import
   */
  async cancelImport(importId: string): Promise<any> {
    try {
      const endpoint = ENDPOINTS.STREAMING_IMPORTS.CANCEL(importId);
      const url = buildApiUrl(endpoint);

      console.log(`Cancelling import at: ${url}`);

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel import');
      }

      return await response.json();
    } catch (error) {
      console.error('Error cancelling import:', error);
      throw error;
    }
  }

  /**
   * Get the current status of an import
   */
  async getImportStatus(importId: string): Promise<any> {
    try {
      const endpoint = ENDPOINTS.STREAMING_IMPORTS.STATUS(importId);
      const url = buildApiUrl(endpoint);

      console.log(`Getting import status from: ${url}`);

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get import status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting import status:', error);
      throw error;
    }
  }

  /**
   * Poll the import status until complete or failed
   */
  async pollUntilComplete(importId: string, onStatusUpdate: (status: Record<string, unknown>) => void,
    pollInterval = 2000, maxAttempts = 300): Promise<Record<string, unknown>> {

    let attempts = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++;

          if (attempts > maxAttempts) {
            reject(new Error('Polling timed out after maximum attempts'));
            return;
          }

          const status = await this.getImportStatus(importId);
          onStatusUpdate(status);

          if (['completed', 'failed', 'cancelled'].includes(status.status)) {
            resolve(status);
            return;
          }

          setTimeout(poll, pollInterval);
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }

  /**
   * Get the final results of an import
   */
  async getImportResults(importId: string): Promise<any> {
    try {
      // Use proper URL construction with buildApiUrl
      const endpoint = `${ENDPOINTS.STREAMING_IMPORTS.PROCESS(importId)}/results`;
      const url = buildApiUrl(endpoint);

      console.log(`Getting import results from: ${url}`);

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get import results');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting import results:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const streamingImportService = new StreamingImportService();